import { message } from 'antd';
import { useEffect, useRef, useState } from 'react';

import { isDev } from '@/utils/envHelper';
import { EventNewID } from '@/utils/eventlog/eventNewConfig';
import { postError, trackEvent } from '@/utils/eventlog/logAdapter';

import { useTranslate } from './useTranslate';

const LATENCY_THRESHOLD = 3000;
const DOWNLINK_THRESHOLD = 1;
const UNSTABLE_TYPES = ['2g', 'slow-2g'];
const BEAT_INTERVAL = 5000;
const TESTURL = '/api/alive';

const useNetworkMonitor = () => {
  const [isOnline, setIsOnline] = useState(true);
  const [isUnstable, setIsUnstable] = useState(false);
  const [networkStatus, setNetworkStatus] = useState('online');
  const intervalRef = useRef<NodeJS.Timer | null>(null);

  const { t } = useTranslate();
  // 检测网络
  const checkStability = async () => {
    if (!isOnline) return;

    const connection =
      // @ts-ignore
      navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
    const effectiveType = connection.effectiveType || 'unknown';
    const downlink = connection.downlink || 0;

    let latency = 0;
    try {
      const start = Date.now();
      const host = window.location.host;
      const path = host.includes('localhost') ? TESTURL : `https://${host + TESTURL}`;
      await fetch(path, { method: 'HEAD', cache: 'no-store' });
      latency = Date.now() - start;
    } catch (e) {
      setIsOnline(false);
      setNetworkStatus('offline');
      trackEvent(EventNewID.PAGE_CRASH, {
        type: 2,
      });
      setIsUnstable(false);
      return;
    }

    const isUnstableNetwork =
      latency > LATENCY_THRESHOLD ||
      UNSTABLE_TYPES.includes(effectiveType) ||
      downlink < DOWNLINK_THRESHOLD;

    if (isUnstableNetwork && !isDev) {
      setIsUnstable(true);
      setNetworkStatus('unstable');
    } else {
      setIsUnstable(false);
      setNetworkStatus('online');
    }
  };
  // 清除定时器
  const clearTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current as any);
    }
  };

  useEffect(() => {
    // 检测在线
    const handleOnline = () => {
      setIsOnline(true);
      setNetworkStatus('online');
    };
    // 检测离线
    const handleOffline = () => {
      if (document.visibilityState === 'hidden') return;
      setIsOnline(false);
      setNetworkStatus('offline');
      setIsUnstable(false);
      trackEvent(EventNewID.PAGE_CRASH, {
        type: 2,
      });
    };
    // 页面可见时重启网络检测
    const handleVisibilityChange = () => {
      console.log('visibilitychange', document.visibilityState);
      if (document.visibilityState === 'visible') {
        console.log('Page is visible, restarting network check');
        checkStability();
        clearTimer();
        intervalRef.current = setInterval(() => {
          checkStability();
        }, BEAT_INTERVAL);
      } else if (intervalRef.current) {
        console.log('Page is hidden, stopping network check');
        clearInterval(intervalRef.current as any);
        intervalRef.current = null;
        setIsOnline(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    clearTimer();
    intervalRef.current = setInterval(checkStability, BEAT_INTERVAL);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimer();
    };
  }, [isOnline]);

  useEffect(() => {
    message.destroy('offlineOrUnstable');
    message.destroy('unstableMessage');
    if (networkStatus === 'offline') {
      console.warn('Network status is offline');
      message.error({
        content: t('Network-issue-please-check-and-retry'),
        duration: 0,
        key: 'offlineOrUnstable',
      });
      postError(new Error('Network-offline'), {
        networkStatus,
        // @ts-ignore
        effectiveType: navigator.connection?.effectiveType,
      });
    } else if (networkStatus === 'unstable') {
      console.warn('Network status is unstable');
      message.warning({
        content: t('Network-unstable'),
        duration: 0,
        key: 'unstableMessage',
      });
      postError(new Error('Network-unstable'), {
        networkStatus,
        // @ts-ignore
        effectiveType: navigator.connection?.effectiveType,
      });
    } else if (networkStatus === 'online') {
      console.log('Network status is online');
    }
  }, [networkStatus]);

  return { isOnline, isUnstable, networkStatus };
};

export { useNetworkMonitor };
